import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdCompany,
  BdCompanyGroup,
} from '@bindoc-projects/resource-domain-lib';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  forkJoin,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { BdShareablesService } from '../resources/core/resources.service';

export interface CompanyScope {
  id: string;
  name: string;
  groups: BdCompanyGroup[];
}

interface StoredCompanyContextRecord {
  companyId: string | null;
  companyName?: string | null;
}

@Injectable({ providedIn: 'root' })
export class BdCompanyContextService {
  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _authService = inject(BdAuthenticationService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _localStorageBasePath =
    'bd.company-context.selected-company';

  private readonly _selectedCompany$ = new BehaviorSubject<
    BdCompany | undefined
  >(undefined);
  private readonly _companies$ = new ReplaySubject<BdCompany[]>(1);
  private readonly _groups$ = new ReplaySubject<BdCompanyGroup[]>(1);

  private _userId: string | undefined;
  private _loaded = false;
  private _restored = false;

  public readonly selectedCompany$: Observable<BdCompany | undefined> =
    this._selectedCompany$.asObservable();

  public readonly selectedCompanyId$: Observable<string | undefined> =
    this.selectedCompany$.pipe(
      map((c) => c?.id),
      distinctUntilChanged(),
    );

  // `companyScopes$` triggers a one-off `loadCompanies()` on first subscription
  // (guarded/idempotent). The global header selector no longer eagerly loads
  // the full list, so the shareables scope sidebar — the remaining consumer —
  // must pull it itself; deferring here guarantees data without re-introducing
  // an eager fetch on pages that never read it.
  public readonly companyScopes$: Observable<CompanyScope[]> = defer(() => {
    this.loadCompanies();
    return combineLatest([this._companies$, this._groups$]).pipe(
      map(([companies, groups]) => this._buildScopes(companies, groups)),
    );
  });

  public readonly selectedCompanyScopes$: Observable<CompanyScope[]> =
    combineLatest([this.companyScopes$, this.selectedCompanyId$]).pipe(
      map(([scopes, selectedId]) =>
        selectedId ? scopes.filter((s) => s.id === selectedId) : scopes,
      ),
    );

  constructor() {
    this._authService.accountInfo$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((accountInfo) => {
        const nextUserId =
          accountInfo?.localAccountId ??
          accountInfo?.homeAccountId ??
          undefined;
        if (nextUserId !== this._userId) {
          this._userId = nextUserId;
          this._restored = false;
        }
      });
  }

  public get currentCompanyId(): string | undefined {
    return this._selectedCompany$.getValue()?.id;
  }

  /**
   * Synchronous snapshot of the active company context, for callers that need
   * the name as well as the id and cannot subscribe — e.g. a modal dialog,
   * which the header selector cannot change while it is open.
   */
  public get currentCompany(): BdCompany | undefined {
    return this._selectedCompany$.getValue();
  }

  public selectCompany(company: BdCompany | undefined): void {
    this._selectedCompany$.next(company);
    this._storeSelection(company);
  }

  /**
   * Restores the previously selected company from local storage **without**
   * fetching the company list. The global company selector lives in the app
   * header on every page, so it uses this to re-establish the context lazily
   * — the full list is only downloaded where it is actually needed (the
   * shareables scope sidebar, via {@link loadCompanies}).
   */
  public restoreSelection(): void {
    this._restoreSelectionFromStorage();
  }

  public loadCompanies(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    forkJoin({
      companies: this._resourcesService.myCompanies(),
      groups: this._resourcesService.myGroups(),
    }).subscribe(({ companies, groups }) => {
      this._restoreSelectionFromStorage(companies);
      this._companies$.next(companies);
      this._groups$.next(groups);
    });
  }

  public _buildScopes(
    companies: BdCompany[],
    groups: BdCompanyGroup[],
  ): CompanyScope[] {
    const groupsByCompany = new Map<string, BdCompanyGroup[]>();
    for (const group of groups) {
      const bucket = groupsByCompany.get(group.companyId) ?? [];
      bucket.push(group);
      groupsByCompany.set(group.companyId, bucket);
    }

    const companyById = new Map(companies.map((c) => [c.id, c]));

    const result: CompanyScope[] = [];
    for (const [companyId, companyGroups] of groupsByCompany) {
      const company = companyById.get(companyId);
      result.push({
        id: companyId,
        name: company?.name ?? companyId,
        groups: companyGroups,
      });
    }

    for (const company of companies) {
      if (!groupsByCompany.has(company.id)) {
        result.push({ id: company.id, name: company.name, groups: [] });
      }
    }

    return result;
  }

  private get _localStoragePath(): string {
    return this._userId
      ? `${this._localStorageBasePath}.${this._userId}`
      : this._localStorageBasePath;
  }

  private _storeSelection(company: BdCompany | undefined): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    const record: StoredCompanyContextRecord = {
      companyId: company?.id ?? null,
      companyName: company?.name ?? null,
    };
    localStorage.setItem(this._localStoragePath, JSON.stringify(record));
  }

  private _restoreSelectionFromStorage(companies?: BdCompany[]): void {
    if (this._restored) {
      return;
    }
    this._restored = true;
    if (typeof localStorage === 'undefined') {
      return;
    }
    const raw = localStorage.getItem(this._localStoragePath);
    if (raw === null) {
      return;
    }
    try {
      const record = JSON.parse(raw) as StoredCompanyContextRecord;
      if (record.companyId === null) {
        this._selectedCompany$.next(undefined);
        return;
      }
      // Prefer the authoritative company from a freshly loaded list (fresh
      // name); otherwise reconstruct it from the persisted id + name so the
      // header can restore context without downloading every company.
      const match =
        companies?.find((c) => c.id === record.companyId) ??
        (record.companyName != null
          ? { id: record.companyId, name: record.companyName }
          : undefined);
      if (match) {
        this._selectedCompany$.next(match);
      }
    } catch {
      // ignore malformed storage entries
    }
  }
}
